import React from 'react';
import PropTypes from 'prop-types';
import { Card } from 'components/base';
import styled, { css } from 'styled-components';
import { rgba } from 'utils/polished';
import classNames from 'classnames';

import Icon, { ICON_TYPE } from '../../components/base/icon';
import IconStyles from '../../components/base/iconStyles';

const MenuButton = styled.button`
  line-height: 18px;
  margin-left: 1px;
  user-select: none;
  ${props =>
    props.color === 'orange'
      ? css`
          color: ${props.theme.colors.white};
          background: #6ba410;

          &:hover {
            background: #54810d;
          }
        `
      : css`
          color: ${props.theme.colors.primary};
          background: ${rgba(props.theme.colors.primary, 0.16)};

          &:hover {
            color: ${props.theme.colors.white};
            background: ${props.theme.colors.primary};
          }
        `}

  span {
    font-size: 12px;
    display: block;
    transition: none;
  }

  ${IconStyles} {
    width: 14px;
    height: 14px;
    margin: 2px 0 0 0;
  }
`;

const Menu = styled.div`
  width: auto;
  border-radius: 0 0 5px 5px;

  ${props =>
    props.withoutLabel &&
    css`
      ${MenuButton}:first-child {
        border-bottom-left-radius: 5px;
        margin-left: 0 !important;
      }
    `}

  ${props => css`
    order: ${props.menuPosition === 'right' ? 2 : 1};
  `}

  ${MenuButton}:last-child {
    border-bottom-right-radius: 5px;
  }

  ${props =>
    css`
      color: ${props.theme.colors.primary};
      background: ${rgba(props.theme.colors.primary, 0.16)};
    `}

  ${props =>
    props.menuPosition === 'left' &&
    css`
      margin: 0 auto 0 10px;
    `}

${props =>
    props.menuPosition === 'right' &&
    css`
      margin: 0 10px 0 auto;
    `}

${props =>
    props.menuPosition === 'center' &&
    css`
      margin: 0 auto 0 auto;
    `}

  bottom: -38px;
  height: 38px;
  display: flex;

  strong {
    padding: 0 10px;
  }
`;

const MenuVertical = styled.div`
  display: flex;
  width: auto;

  ${({ horizontalPosition }) =>
    horizontalPosition === 'left'
      ? css`
          border-radius: 5px 0 0 5px;

          ${({ withoutLabel }) =>
            withoutLabel &&
            css`
              ${MenuButton}:first-child {
                border-top-left-radius: 5px;
              }
            `}

          ${MenuButton}:last-child {
            border-bottom-left-radius: 5px;
          }
        `
      : css`
          border-radius: 0 5px 5px 0;

          ${({ withoutLabel }) =>
            withoutLabel &&
            css`
              ${MenuButton}:first-child {
                border-top-right-radius: 5px;
              }
            `}

          ${MenuButton}:last-child {
            border-bottom-right-radius: 5px;
          }
        `}

  ${MenuButton} {
    margin-left: 0 !important;
  }

  ${props =>
    css`
      color: ${props.theme.colors.primary};
      background: ${rgba(props.theme.colors.primary, 0.16)};
    `}

  ${props =>
    props.verticalPosition === 'top' &&
    css`
      margin-top: 5px;
    `}

  ${props =>
    props.verticalPosition === 'bottom' &&
    css`
      margin-bottom: 5px;
    `}
`;

const PageWrapper = props => {
  const handleOnItemClick = (e, action, data) => {
    e.preventDefault();

    props.onMenuItemClick(action, data);
  };

  let buttons;
  if (props.editable) {
    buttons = [
      <MenuButton key="edit" onClick={e => handleOnItemClick(e, 'edit')}>
        <Icon type={ICON_TYPE.EDIT} />
        <span>EDIT</span>
      </MenuButton>,
    ];

    if (!props.isCover && !props.editOnly) {
      if (!props.isTOCPage || props.isTOCFirstPage) {
        // add works only for firstTOCpage or every other page
        buttons.push(
          <MenuButton key="add" onClick={e => handleOnItemClick(e, 'add')}>
            <Icon type={ICON_TYPE.ADD} />
            <span>ADD</span>
          </MenuButton>,
        );
      }
      if (props.isTOCPage) {
        // reload works only for TOC
        buttons.push(
          <MenuButton key="refresh" onClick={e => handleOnItemClick(e, 'refreshToc')}>
            <Icon type={ICON_TYPE.REFRESH} />
            <span>RELOAD</span>
          </MenuButton>,
        );
      }
      if (!props.isTOCPage) {
        // clone doesn't work for TOC
        buttons.push(
          <MenuButton key="clone" onClick={e => handleOnItemClick(e, 'clone')}>
            <Icon type={ICON_TYPE.CLONE} />
            <span>CLONE</span>
          </MenuButton>,
        );
      }
      if (!props.isTOCPage || props.isTOCFirstPage) {
        // move works only for firstTOCpage or every other page
        buttons.push(
          <MenuButton key="move" onClick={e => handleOnItemClick(e, 'move')}>
            <Icon type={ICON_TYPE.MOVE} />
            <span>MOVE</span>
          </MenuButton>,
        );
      }

      buttons.push(
        <MenuButton key="delete" onClick={e => handleOnItemClick(e, 'delete')}>
          <Icon type={ICON_TYPE.BIN} />
          <span>DELETE</span>
        </MenuButton>,
      );
    }
  }

  let menu;
  let className;
  if (!props.noMenu) {
    if (props.menuPosition === 'side') {
      className = props.buttonPosition === 'left' ? 'flex-row-reverse' : 'flex-row';
      menu = (
        <div
          className={classNames(
            'd-flex flex-column justify-content-between',
            props.buttonPosition === 'left' ? 'align-items-end' : 'align-items-start',
          )}
        >
          <div>
            {props.persons.length > 0 && (
              <MenuVertical
                verticalPosition="top"
                horizontalPosition={props.buttonPosition}
                withoutLabel
              >
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <MenuButton
                    key="records"
                    color="orange"
                    onClick={e => handleOnItemClick(e, 'records', { persons: props.persons })}
                  >
                    <Icon type={ICON_TYPE.MAGIC} />
                    <span>FIND RECORDS</span>
                  </MenuButton>
                </div>
              </MenuVertical>
            )}
          </div>
          <MenuVertical
            verticalPosition="bottom"
            horizontalPosition={props.buttonPosition}
            withoutLabel
          >
            <div className="d-flex flex-column">
              <strong
                className="d-flex align-items-center justify-content-center"
                style={{ height: 38, padding: '0 8px' }}
              >
                {props.label}
              </strong>
              {buttons}
            </div>
          </MenuVertical>
        </div>
      );
    } else {
      menu = (
        <div className="d-flex">
          <Menu menuPosition={props.buttonPosition}>
            <div className="d-flex align-items-center">
              <strong>{props.label}</strong>
              {buttons}
            </div>
          </Menu>
          {props.persons.length > 0 && (
            <Menu menuPosition={props.buttonPosition === 'left' ? 'right' : 'left'} withoutLabel>
              <div className="d-flex align-items-center">
                <MenuButton
                  key="records"
                  color="orange"
                  onClick={e => handleOnItemClick(e, 'records', { persons: props.persons })}
                >
                  <Icon type={ICON_TYPE.MAGIC} />
                  <span>FIND RECORDS</span>
                </MenuButton>
              </div>
            </Menu>
          )}
        </div>
      );
    }
  }

  return (
    <Card
      id={props.id}
      className={classNames('card-page mb-0 mx-auto w-100', className, props.className)}
    >
      {props.children}
      {menu}
    </Card>
  );
};

PageWrapper.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  buttonPosition: PropTypes.oneOf(['left', 'center', 'right']),
  menuPosition: PropTypes.oneOf(['bottom', 'side']),
  noMenu: PropTypes.bool,
  editable: PropTypes.bool,
  editOnly: PropTypes.bool,
  isCover: PropTypes.bool,
  isTOCPage: PropTypes.bool,
  isTOCFirstPage: PropTypes.bool,
  onMenuItemClick: PropTypes.func,
  persons: PropTypes.array,
  children: PropTypes.node,
};

PageWrapper.defaultProps = {
  label: 'Page',
  buttonPosition: 'right',
  menuPosition: 'bottom',
  noMenu: false,
  editable: true,
  isCover: false,
  isTOCPage: false,
  isTOCFirstPage: false,
  editOnly: false,
  persons: [],
};

export default PageWrapper;
